import { useEffect } from "react";
import { useAuthStore } from "../stores/auth";
import { authApi } from "../api/auth";
import { notify } from "@/lib/utils/toast";

export const useAuth = () => {
  const {
    token,
    user,
    isAuthenticated,
    isLoading,
    setAuth,
    clearAuth,
    setLoading,
    hasPermission,
    isAdmin,
  } = useAuthStore();

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);

      if (!authApi || typeof authApi.login !== "function") {
        throw new Error("Authentication service not available");
      }

      const response = await authApi.login({ email, password });

      if (response && response.success && response.data) {
        setAuth(response.data.token, response.data.user);
        notify.success(response.message || "Đăng nhập thành công");
        return { success: true };
      } else {
        throw new Error(response?.message || "Đăng nhập thất bại");
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Đăng nhập thất bại";
      console.warn("Login error:", message);
      notify.error(
        "Không thể đăng nhập. Vui lòng kiểm tra thông tin và thử lại."
      );
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Microsoft login function
  const loginWithMicrosoft = async (
    code: string,
    state?: string,
    codeVerifier?: string
  ) => {
    try {
      setLoading(true);

      if (!authApi || typeof authApi.microsoftLogin !== "function") {
        throw new Error("Authentication service not available");
      }

      const response = await authApi.microsoftLogin({
        code,
        state,
        codeVerifier,
      });

      if (response && response.success && response.data) {
        setAuth(response.data.token, response.data.user);
        notify.success(response.message || "Đăng nhập Microsoft thành công");
        return { success: true };
      } else {
        throw new Error(response?.message || "Đăng nhập Microsoft thất bại");
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Đăng nhập Microsoft thất bại";
      console.warn("Microsoft login error:", message);
      notify.error("Dịch vụ đăng nhập không khả dụng. Vui lòng thử lại sau.");
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authApi.logout();
      clearAuth();
      notify.success("Đăng xuất thành công");
    } catch (error) {
      // Even if API call fails, clear local auth
      clearAuth();
      console.error("Logout error:", error);
    }
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      const response = await authApi.refreshToken();
      if (response.success && response.data && user) {
        setAuth(response.data.token, user);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Refresh token error:", error);
      clearAuth();
      return false;
    }
  };

  // Verify token on app start
  const verifyToken = async () => {
    if (!token) return false;

    try {
      const response = await authApi.verifyToken();
      if (response.success && response.data?.valid) {
        return true;
      } else {
        clearAuth();
        return false;
      }
    } catch (error) {
      console.error("Token verification error:", error);
      clearAuth();
      return false;
    }
  };

  // Check if user has specific permission
  const checkPermission = (permission: string): boolean => {
    return hasPermission(permission);
  };

  // Check if user is admin
  const checkIsAdmin = (): boolean => {
    return isAdmin();
  };

  // Auto-verify token on mount
  useEffect(() => {
    if (token && !isAuthenticated) {
      verifyToken();
    }
  }, [token, isAuthenticated, verifyToken]);

  return {
    // State
    user,
    token,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    loginWithMicrosoft,
    logout,
    refreshToken,
    verifyToken,

    // Utilities
    hasPermission: checkPermission,
    isAdmin: checkIsAdmin,
  };
};

export default useAuth;
